/**
 * WebSocket Context for Real-Time Price Updates
 * Provides live price feeds to all trading components
 */
import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';

const WebSocketContext = createContext(null);

const getWebSocketUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}`;
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  return backendUrl.replace('https://', 'wss://').replace('http://', 'ws://');
};

// WebSocket endpoints use /api prefix for routing through the proxy
const WS_ENDPOINTS = {
  prices: '/api/ws/prices',
  trading: '/api/ws/trading',
  manager: '/api/ws/manager',
};

export const WebSocketProvider = ({ children, tokenKey = 'trader_token', enabled = true }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [prices, setPrices] = useState({});
  const [positions, setPositions] = useState([]);
  const [accountData, setAccountData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const lastMessageRef = useRef(0);
  const authRejectedRef = useRef(false);
  const subscriptionsRef = useRef(new Set());
  const activeRef = useRef(false);
  
  const connect = useCallback(() => {
    activeRef.current=true;
    clearTimeout(reconnectTimeoutRef.current);
    const token = localStorage.getItem(tokenKey);
    const wsUrl = getWebSocketUrl();
    
    if (!wsUrl) {
      console.error('WebSocket URL not configured');
      return;
    }
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.onclose=null;
      wsRef.current.onmessage=null;
      wsRef.current.onopen=null;
      wsRef.current.close();
    }
    
    // Use /api prefix for routing through the proxy
    const endpoint = token 
      ? `${wsUrl}${WS_ENDPOINTS.trading}?token=${token}` 
      : `${wsUrl}${WS_ENDPOINTS.prices}`;
    
    try {
      wsRef.current = new WebSocket(endpoint);
      const socket=wsRef.current;
      
      wsRef.current.onopen = () => {
        if(!activeRef.current||wsRef.current!==socket)return;
        console.log('WebSocket connected');
        setIsConnected(true);
        lastMessageRef.current = Date.now();
        authRejectedRef.current = false;
        reconnectAttempts.current = 0;
        
        // Resubscribe to previous symbols
        if (subscriptionsRef.current.size > 0) {
          const symbols = Array.from(subscriptionsRef.current);
          wsRef.current.send(JSON.stringify({
            action: 'subscribe',
            symbols
          }));
        }
      };
      
      wsRef.current.onmessage = (event) => {
        if(!activeRef.current||wsRef.current!==socket)return;
        lastMessageRef.current = Date.now();
        try {
          const message = JSON.parse(event.data);
          
          switch (message.type) {
            case 'session_revoked':
              authRejectedRef.current=true;
              setPrices({});setPositions([]);setAccountData(null);
              if(tokenKey==='trader_token')window.dispatchEvent(new CustomEvent('trader-session-revoked',{detail:{token}}));
              break;
            case 'price_update':
              setPrices(prev => ({
                ...prev,
                ...message.data
              }));
              setLastUpdate(message.timestamp);
              break;
              
            case 'position_update':
              setPositions(message.positions || []);
              break;
              
            case 'account_update':
              setAccountData(message.data);
              break;
              
            case 'positions':
              setPositions(message.data || []);
              break;
              
            case 'connected':
              console.log('WebSocket authenticated:', message.user_id);
              break;
              
            case 'subscribed':
              console.log('Subscribed to:', message.symbols);
              break;
              
            case 'pong':
              // Heartbeat response
              break;
              
            default:
              console.log('Unknown message type:', message.type);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        if(!activeRef.current||wsRef.current!==socket)return;
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        authRejectedRef.current = [4001,4002,4003].includes(event.code);
        if(authRejectedRef.current&&tokenKey==='trader_token')window.dispatchEvent(new CustomEvent('trader-session-revoked',{detail:{token}}));
        
        // Attempt reconnection
        if (activeRef.current && !authRejectedRef.current) {
          const delay = Math.min(1000 * Math.pow(2, Math.min(reconnectAttempts.current, 5)), 30000);
          console.log(`Reconnecting in ${delay}ms...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [tokenKey]);
  
  const disconnect = useCallback(() => {
    activeRef.current=false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.onclose=null;
      wsRef.current.onmessage=null;
      wsRef.current.onopen=null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setAccountData(null);setPositions([]);
  }, []);
  
  const subscribe = useCallback((symbols) => {
    const symbolArray = Array.isArray(symbols) ? symbols : [symbols];
    symbolArray.forEach(s => subscriptionsRef.current.add(s));
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'subscribe',
        symbols: symbolArray
      }));
    }
  }, []);
  
  const unsubscribe = useCallback((symbols) => {
    const symbolArray = Array.isArray(symbols) ? symbols : [symbols];
    symbolArray.forEach(s => subscriptionsRef.current.delete(s));
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'unsubscribe',
        symbols: symbolArray
      }));
    }
  }, []);
  
  const getPrice = useCallback((symbol) => {
    return prices[symbol] || null;
  }, [prices]);
  
  const requestPositions = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        action: 'get_positions'
      }));
    }
  }, []);
  
  const sendPing = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      if(Date.now()-lastMessageRef.current>60000){wsRef.current.close();return;}
      wsRef.current.send(JSON.stringify({ action: 'ping' }));
    }
  }, []);
  
  // Auto-connect on mount
  useEffect(() => {
    if(!enabled)return;
    const start=setTimeout(connect,0);
    
    // Heartbeat to keep connection alive
    const heartbeat = setInterval(sendPing, 30000);
    const resume = () => {
      if(!activeRef.current||authRejectedRef.current||document.visibilityState!=='visible'||!navigator.onLine)return;
      const socket=wsRef.current;
      if(!socket||socket.readyState===WebSocket.CLOSED){connect();return;}
      if(socket.readyState===WebSocket.OPEN){
        if(Date.now()-lastMessageRef.current>60000){connect();return;}
        sendPing();requestPositions();
      }
    };
    window.addEventListener('online',resume);
    window.addEventListener('focus',resume);
    document.addEventListener('visibilitychange',resume);
    
    return () => {
      clearTimeout(start);
      clearInterval(heartbeat);
      window.removeEventListener('online',resume);
      window.removeEventListener('focus',resume);
      document.removeEventListener('visibilitychange',resume);
      disconnect();
    };
  }, [connect, disconnect, sendPing, requestPositions, enabled]);
  
  const value = {
    isConnected,
    prices,
    positions,
    accountData,
    lastUpdate,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    getPrice,
    requestPositions,
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

// Hook for getting real-time price of a specific symbol
export const usePrice = (symbol) => {
  const { prices, subscribe, isConnected } = useWebSocket();
  
  useEffect(() => {
    if (symbol && isConnected) {
      subscribe(symbol);
    }
  }, [symbol, subscribe, isConnected]);
  
  return prices[symbol] || null;
};

// Hook for getting multiple prices
export const usePrices = (symbols = []) => {
  const { prices, subscribe, isConnected } = useWebSocket();
  
  useEffect(() => {
    if (symbols.length > 0 && isConnected) {
      subscribe(symbols);
    }
  }, [symbols, subscribe, isConnected]);
  
  return symbols.reduce((acc, symbol) => {
    acc[symbol] = prices[symbol] || null;
    return acc;
  }, {});
};

export default WebSocketContext;
